import type { TermStatement } from "../../../api/content/ontologyApi";
import type { EditSession } from "../../editing";
import { readDraft, type TermEditRequest } from "../proposals/termEditDraft";
import type { TermEdit } from "../proposals/termPatches";

/**
 * A term's labels and definitions edited in place (F96), kept as term edits until one Save
 * proposes them all (`termEditCommit`). An existing statement is named by its triple; a new
 * one by its section and a number.
 */
export type StatementSection = "label" | "definition";

export const statementKey = (statement: TermStatement): string => `${statement.predicate} ${statement.language ?? ""} ${statement.text}`;

const ADDED_PREFIX = "added:";
const addedKey = (section: StatementSection, number: number): string => `${ADDED_PREFIX}${section}:${String(number)}`;

/** A key for one more new statement of `section`, never one an unsaved one holds. */
export function nextAddedKey(session: EditSession<TermEdit>, section: StatementSection): string {
  const numbers = addedStatements(session, section).map((staged) => Number(staged.key.slice(staged.key.lastIndexOf(":") + 1)));
  return addedKey(section, Math.max(0, ...numbers) + 1);
}

export const isAddedKey = (key: string, section: StatementSection): boolean => key.startsWith(`${ADDED_PREFIX}${section}:`);

/** The new statements of `section` waiting to be saved, by key, in the order they were added. */
export function addedStatements(session: EditSession<TermEdit>, section: StatementSection): readonly { readonly key: string; readonly edit: TermEdit }[] {
  return session.edits.filter((staged) => isAddedKey(staged.key, section));
}

/** The unsaved text of an edit: what the statement will say. */
export function stagedText(edit: TermEdit | undefined): string | undefined {
  if (edit === undefined) return undefined;
  return edit.kind === "addSubclass" ? edit.label : edit.text;
}

/** The language a new statement was given. */
export function stagedLanguage(edit: TermEdit | undefined): string | null {
  return edit !== undefined && (edit.kind === "addLabel" || edit.kind === "editDefinition") ? edit.language : null;
}

/** What replacing `statement` with `text` is; for a new one (`null`), adding it in `language`. */
export function requestFor(section: StatementSection, statement: TermStatement | null): TermEditRequest {
  if (section === "label") return statement === null ? { kind: "addLabel" } : { kind: "renameLabel", statement };
  return { kind: "editDefinition", statement };
}

/** Make `text` in `language` the unsaved edit of `key`; one that can't be proposed keeps its problem, which blocks the save. */
export function stageStatement(
  session: EditSession<TermEdit>,
  key: string,
  request: TermEditRequest,
  text: string,
  language: string,
  termIri: string,
): void {
  const { edit, problem } = readDraft(request, { text, language, iri: null }, termIri, new Set());
  session.stage(key, edit, problem);
}
